import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import {
  canRedoAny,
  canUndoAny,
  redoLast,
  undoLast,
} from '../features/history/unifiedHistory';
import {
  duplicateElements,
  updateElements,
  addElements,
  setStockerPortEdit,
} from '../features/layout/layoutSlice';
import { addHistoryAction } from '../features/history/historySlice';
import { selectAll, selectElements, clearSelection } from '../features/selection/selectionSlice';
import { toggleGrid, setZoomLevel } from '../features/ui/uiSlice';
import { viewportStore } from '../store/viewportStore';
import { exportToJSON } from '../utils/storage';
import {
  buildLayoutSaveProject,
  createLayoutExportBase,
  fabExportFilename,
} from '../utils/projectExport';
import { selectExportableEquipmentInstances } from '../features/equipment/equipmentSelectors';
import { generateId } from '../utils/geometry';
import {
  collectExistingEntityIds,
  createEntityIdAllocator,
} from '../utils/entityIds';
import {
  deleteSelectedLayoutElements,
  deleteSelectedTrackNode,
} from '../utils/deleteCanvasSelection';
import { hasTextSelection } from '../utils/textSelection';
import { MIN_ZOOM, MAX_ZOOM, ZOOM_STEP, DEFAULT_ZOOM } from '../constants';
import { LayoutElement } from '../types';

// Module-level clipboard — survives re-renders, lives for the browser session.
let clipboard: LayoutElement[] = [];
// Tracks how many times Ctrl+V has been pressed since the last Ctrl+C, so each
// successive paste shifts elements slightly instead of stacking on top.
let pasteCount = 0;

/**
 * Custom hook that registers global keyboard shortcuts.
 * Supports: undo/redo, copy/paste, select all, delete, grid toggle, zoom, export.
 * Handles multi-press paste offsets so repeated pastes don't stack on top of each other.
 * @example
 * function MyComponent() {
 *   useKeyboardShortcuts();
 *   return <div>Canvas</div>;
 * }
 */
export const useKeyboardShortcuts = () => {
  const dispatch = useAppDispatch();
  const selectedIds = useAppSelector((state) => state.selection.selectedIds);
  const selectedTrackNodeId = useAppSelector((state) => state.layout.selectedTrackNodeId);
  const elements = useAppSelector((state) => state.layout.elements);
  const portEditElementId = useAppSelector((state) => state.layout.portEditElementId);
  const elementOrder = useAppSelector((state) => state.layout.elementOrder);
  const trackNodeAnnotations = useAppSelector((state) => state.layout.trackNodeAnnotations);
  const activeLayerId = useAppSelector((state) => state.layout.activeLayerId);
  const layoutLayers = useAppSelector((state) => state.layout.layers);
  const canUndo = useAppSelector(canUndoAny);
  const canRedo = useAppSelector(canRedoAny);
  const zoomLevel = useAppSelector((state) => state.ui.zoomLevel);
  const gridSettings = useAppSelector((state) => state.ui.gridSettings);
  const layersState = useAppSelector((state) => state.layers);
  const equipmentInstances = useAppSelector((state) => state.equipment.instances);
  // Ctrl+S saves only overlays the user can see (see equipmentSelectors). ID
  // allocation still needs every instance so new ids cannot collide.
  const exportableEquipmentInstances = useAppSelector(selectExportableEquipmentInstances);
  const equipmentTemplates = useAppSelector((state) => state.equipment.templates);
  const equipmentVisible = useAppSelector((state) => state.equipment.visible);
  const equipmentShowLabels = useAppSelector((state) => state.equipment.showLabels);
  const equipmentShowPorts = useAppSelector((state) => state.equipment.showPorts);
  const layoutSaveMarkedIds = useAppSelector((state) => state.equipment.layoutSaveMarkedIds);
  const floorPlanName = useAppSelector((state) => state.floorplan.name);
  const floorPlanId = useAppSelector((state) => state.floorplan.floorPlanId);
  const floorPlanPolylineCount = useAppSelector((state) => state.floorplan.polylines.length);
  // Simulator is read-only, so shortcuts that select or delete stay inert there.
  const simulationMode = useAppSelector((state) => state.ui.simulationPanelOpen);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      // Check if user is typing in an input field
      const target = e.target as HTMLElement;
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (isTyping) return;

      // Highlighted text anywhere in the UI means copy/select-all belong to the
      // browser, not the canvas clipboard.
      if (
        isCtrlOrCmd &&
        (e.key.toLowerCase() === 'c' || e.key.toLowerCase() === 'a') &&
        hasTextSelection()
      ) {
        return;
      }

      // Prevent browser behavior only when the canvas shortcut will handle it.
      // Native text shortcuts (select all, copy, paste, undo) must remain
      // available in inputs and textareas such as the JSON configuration editor.
      const preventKeys = ['z', 'y', 'c', 'v', 'd', 'a', 'g', 's', '=', '-', '0'];
      if (isCtrlOrCmd && preventKeys.includes(e.key.toLowerCase())) {
        e.preventDefault();
      }

      // Undo: Ctrl+Z. Spans the layout and equipment stacks so the most recent
      // edit is reversed regardless of which layer it belongs to.
      if (isCtrlOrCmd && e.key.toLowerCase() === 'z' && !e.shiftKey && canUndo) {
        dispatch(undoLast());
        return;
      }

      // Redo: Ctrl+Shift+Z or Ctrl+Y
      if (
        ((isCtrlOrCmd && e.key.toLowerCase() === 'z' && e.shiftKey) ||
          (isCtrlOrCmd && e.key.toLowerCase() === 'y')) &&
        canRedo
      ) {
        dispatch(redoLast());
        return;
      }

      // Delete: Delete or Backspace (layout elements or selected track node)
      if (!simulationMode && (e.key === 'Delete' || e.key === 'Backspace')) {
        if (selectedTrackNodeId) {
          e.preventDefault();
          deleteSelectedTrackNode(
            dispatch,
            selectedTrackNodeId,
            elements,
            Object.values(equipmentInstances)
          );
          return;
        }
        if (selectedIds.length > 0) {
          e.preventDefault();
          deleteSelectedLayoutElements(dispatch, selectedIds, elements);
          return;
        }
      }

      // Nudge selection with arrow keys. Default to very fine world-unit
      // movement for DXF-scale alignment; Shift keeps the previous coarse step.
      if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) &&
        selectedIds.length > 0
      ) {
        e.preventDefault();
        const step = e.shiftKey
          ? gridSettings.snapToGrid
            ? gridSettings.snapGridSize
            : 1
          : 0.01;
        let dx = 0;
        let dy = 0;
        if (e.key === 'ArrowUp') dy = -step;
        else if (e.key === 'ArrowDown') dy = step;
        else if (e.key === 'ArrowLeft') dx = -step;
        else if (e.key === 'ArrowRight') dx = step;

        const movable = selectedIds.filter((id) => {
          const el = elements[id];
          return el && !el.metadata.locked;
        });
        if (movable.length === 0) return;

        dispatch(
          addHistoryAction({
            id: generateId(),
            type: 'move',
            timestamp: Date.now(),
            elementIds: movable,
            previousState: movable.map((id) => elements[id]),
          })
        );
        dispatch(
          updateElements(
            movable.map((id) => {
              const el = elements[id];
              return {
                id,
                updates: {
                  position: { x: el.position.x + dx, y: el.position.y + dy },
                },
              };
            })
          )
        );
        return;
      }

      // Copy: Ctrl+C
      if (isCtrlOrCmd && e.key.toLowerCase() === 'c') {
        const toCopy = selectedIds
          .map((id) => elements[id])
          .filter((el): el is LayoutElement => !!el);
        if (toCopy.length === 0) return;
        // Deep-clone so future edits to the originals don't mutate the clipboard.
        clipboard = toCopy.map((el) => JSON.parse(JSON.stringify(el)));
        pasteCount = 0;
        return;
      }

      // Paste: Ctrl+V
      if (isCtrlOrCmd && e.key.toLowerCase() === 'v') {
        if (clipboard.length === 0) return;
        pasteCount += 1;
        const offset = pasteCount * 20;
        const idAlloc = createEntityIdAllocator(
          collectExistingEntityIds(Object.values(elements), Object.values(equipmentInstances))
        );
        const newElements: LayoutElement[] = clipboard.map((el) => ({
          ...el,
          id: idAlloc.nextForElement(
            el.type,
            typeof el.customProperties?.railType === 'string'
              ? el.customProperties.railType
              : undefined
          ),
          position: {
            x: el.position.x + offset,
            y: el.position.y + offset,
          },
          metadata: { ...el.metadata },
        }));
        dispatch(
          addHistoryAction({
            id: generateId(),
            type: 'add',
            timestamp: Date.now(),
            elementIds: newElements.map((el) => el.id),
            previousState: [],
          })
        );
        dispatch(addElements(newElements));
        dispatch(selectElements(newElements.map((el) => el.id)));
        return;
      }

      // Duplicate: Ctrl+D
      if (isCtrlOrCmd && e.key.toLowerCase() === 'd') {
        const activeLayerIds = layoutLayers[activeLayerId]?.elementIds ?? [];
        const duplicateIds =
          selectedIds.length > 0
            ? selectedIds
            : activeLayerIds.filter((id) => !!elements[id]);

        if (duplicateIds.length === 0) return;
        dispatch(duplicateElements(duplicateIds));
        return;
      }

      // Select All: Ctrl+A
      if (isCtrlOrCmd && e.key.toLowerCase() === 'a') {
        if (!simulationMode) dispatch(selectAll(elementOrder));
        return;
      }

      // Deselect: Escape (exits stocker port-edit mode first, if active)
      if (e.key === 'Escape') {
        if (portEditElementId) {
          dispatch(setStockerPortEdit(null));
          return;
        }
        dispatch(clearSelection());
        return;
      }

      // Toggle Grid: Ctrl+G
      if (isCtrlOrCmd && e.key.toLowerCase() === 'g') {
        dispatch(toggleGrid());
        return;
      }

      // Save/Export: Ctrl+S — layout elements + equipment overlay layer
      if (isCtrlOrCmd && e.key.toLowerCase() === 's') {
        const layoutBase = createLayoutExportBase({
          elements,
          elementOrder,
          layers: layersState.layers,
          layerOrder: layersState.layerOrder,
          groups: layersState.groups,
          viewport: { x: 0, y: 0, scale: zoomLevel },
          gridSettings,
          trackNodeAnnotations,
        });
        const project = buildLayoutSaveProject({
          layoutBase,
          templates: Object.values(equipmentTemplates),
          instances: exportableEquipmentInstances,
          markedIds: layoutSaveMarkedIds,
          floorPlanId,
          floorPlanName,
          equipmentUi: {
            visible: equipmentVisible,
            showLabels: equipmentShowLabels,
            showPorts: equipmentShowPorts,
          },
        });
        exportToJSON(project, fabExportFilename(floorPlanName));
        return;
      }

      // Zoom In: Ctrl++
      if (isCtrlOrCmd && (e.key === '=' || e.key === '+')) {
        const nextZoom = Math.min(MAX_ZOOM, zoomLevel + ZOOM_STEP);
        dispatch(setZoomLevel(nextZoom));
        viewportStore.setState({ zoom: nextZoom });
        return;
      }

      // Zoom Out: Ctrl+-
      if (isCtrlOrCmd && e.key === '-') {
        const nextZoom = Math.max(MIN_ZOOM, zoomLevel - ZOOM_STEP);
        dispatch(setZoomLevel(nextZoom));
        viewportStore.setState({ zoom: nextZoom });
        return;
      }

      // Reset Zoom: Ctrl+0 — 700% without DXF; 100% when a floor plan is loaded
      if (isCtrlOrCmd && e.key === '0') {
        const nextZoom = floorPlanPolylineCount > 0 ? 1 : DEFAULT_ZOOM;
        dispatch(setZoomLevel(nextZoom));
        viewportStore.setState({ zoom: nextZoom });
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    dispatch,
    selectedIds,
    selectedTrackNodeId,
    portEditElementId,
    elementOrder,
    canUndo,
    canRedo,
    elements,
    activeLayerId,
    layoutLayers,
    zoomLevel,
    gridSettings,
    trackNodeAnnotations,
    layersState.layers,
    layersState.layerOrder,
    layersState.groups,
    equipmentInstances,
    exportableEquipmentInstances,
    equipmentTemplates,
    equipmentVisible,
    equipmentShowLabels,
    equipmentShowPorts,
    layoutSaveMarkedIds,
    floorPlanName,
    floorPlanId,
    floorPlanPolylineCount,
    simulationMode,
  ]);
};
